import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError, fileUrl } from "@/lib/api";
import { PageHeader, Surface, Badge, Button, Modal, Select, Input } from "@/components/ui-kit";
import VehicleSearchSelect from "@/components/VehicleSearchSelect";
import { ShieldCheck, UploadSimple, Sparkle, FileText, ArrowsClockwise, Trash, Funnel, PencilSimple, DownloadSimple, Printer } from "@phosphor-icons/react";
import { formatDate, daysUntil } from "@/lib/format";
import { downloadCsv, printElement } from "@/lib/csv";

const TYPES = [
    { key: "insurance", label: "Insurance" },
    { key: "puc", label: "PUC" },
    { key: "rc", label: "RC" },
    { key: "fitness", label: "Fitness" },
    { key: "permit", label: "Permit" },
    { key: "permit_national", label: "Permit · National", parent: "permit" },
    { key: "permit_tn", label: "Permit · TN", parent: "permit" },
    { key: "permit_ap", label: "Permit · AP", parent: "permit" },
    { key: "permit_ka", label: "Permit · KA", parent: "permit" },
    { key: "permit_kl", label: "Permit · KL", parent: "permit" },
    { key: "road_tax", label: "Road Tax" },
    { key: "road_tax_tn", label: "Road Tax · TN", parent: "road_tax" },
    { key: "road_tax_ap", label: "Road Tax · AP", parent: "road_tax" },
    { key: "road_tax_kl", label: "Road Tax · KL", parent: "road_tax" },
    { key: "road_tax_kl_welfare", label: "Road Tax · KL Welfare", parent: "road_tax" },
    { key: "road_tax_ka", label: "Road Tax · KA", parent: "road_tax" },
    { key: "driving_license", label: "Driver License" },
];

// Variants picker — when user picks Permit/Road Tax, expose the sub-divisions.
const PERMIT_SUBS = TYPES.filter((t) => t.parent === "permit");
const ROAD_TAX_SUBS = TYPES.filter((t) => t.parent === "road_tax");

function isParentType(key) { return key === "permit" || key === "road_tax"; }
function getSubsFor(parentKey) {
    if (parentKey === "permit") return PERMIT_SUBS;
    if (parentKey === "road_tax") return ROAD_TAX_SUBS;
    return [];
}

export default function Compliance() {
    const [docs, setDocs] = useState([]);
    const [vehicles, setVehicles] = useState([]);
    const [drivers, setDrivers] = useState([]);
    const [filterType, setFilterType] = useState("all");
    const [filterStatus, setFilterStatus] = useState("all");
    const [filterHorizon, setFilterHorizon] = useState("all"); // all | 30 | 60 | 90 | expired
    const [filterVehicle, setFilterVehicle] = useState(""); // vehicle_id filter (empty = any)
    const [uploadOpen, setUploadOpen] = useState(false);
    const [uploadForm, setUploadForm] = useState({ doc_type: "insurance", target: "vehicle", vehicle_id: "", driver_id: "" });
    const [uploadFile, setUploadFile] = useState(null);
    const [editDoc, setEditDoc] = useState(null);
    const [editForm, setEditForm] = useState({ document_number: "", issue_date: "", expiry_date: "", holder_name: "", hazardous_expiry_date: "", notes: "" });
    const [missing, setMissing] = useState(null); // { total_vehicles, categories: [...] }
    const [missingDrillFor, setMissingDrillFor] = useState(null); // category object

    const load = async () => {
        const r = await api.get("/compliance-documents");
        setDocs(r.data);
        try {
            const m = await api.get("/compliance-documents/missing-summary");
            setMissing(m.data);
        } catch (e) { setMissing(null); }
    };

    useEffect(() => {
        load();
        api.get("/vehicles").then((r) => setVehicles(r.data));
        api.get("/drivers").then((r) => setDrivers(r.data));
    }, []);

    const vmap = Object.fromEntries(vehicles.map((v) => [v.id, v]));
    const dmap = Object.fromEntries(drivers.map((d) => [d.id, d]));
    const today = new Date();

    const expiryStatus = (date) => {
        if (!date) return { variant: "cancelled", label: "NOT SET", days: null };
        const days = daysUntil(date);
        if (days < 0) return { variant: "critical", label: `EXPIRED ${-days}d`, days };
        if (days <= 30) return { variant: "warning", label: `DUE ${days}d`, days };
        return { variant: "healthy", label: `OK ${days}d`, days };
    };

    const filtered = useMemo(() => {
        return docs.filter((d) => {
            if (filterType !== "all" && d.doc_type !== filterType) return false;
            if (filterStatus !== "all") {
                const s = expiryStatus(d.expiry_date);
                if (s.variant !== filterStatus) return false;
            }
            if (filterVehicle && d.vehicle_id !== filterVehicle) return false;
            if (filterHorizon !== "all") {
                const days = d.expiry_date ? daysUntil(d.expiry_date) : null;
                if (days === null) return false;
                if (filterHorizon === "expired") {
                    if (days >= 0) return false;
                } else {
                    const limit = parseInt(filterHorizon, 10);
                    if (days < 0 || days > limit) return false;
                }
            }
            return true;
        });
    }, [docs, filterType, filterStatus, filterHorizon, filterVehicle]);

    const exportCsv = () => {
        downloadCsv(`compliance-${new Date().toISOString().slice(0, 10)}.csv`, filtered, [
            ["TYPE", (d) => TYPES.find((t) => t.key === d.doc_type)?.label || d.doc_type],
            ["DOC #", "document_number"],
            ["VEHICLE / DRIVER", (d) => vmap[d.vehicle_id]?.reg_number || dmap[d.driver_id]?.name || ""],
            ["HOLDER NAME", "holder_name"],
            ["HAZARDOUS EXPIRY", (d) => formatDate(d.hazardous_expiry_date)],
            ["ISSUE DATE", (d) => formatDate(d.issue_date)],
            ["EXPIRY DATE", (d) => formatDate(d.expiry_date)],
            ["DAYS LEFT", (d) => (d.expiry_date ? daysUntil(d.expiry_date) : "")],
            ["STATUS", (d) => expiryStatus(d.expiry_date).label],
            ["AI", (d) => (d.ai_status || "").toUpperCase()],
            ["MANUALLY EDITED", (d) => (d.manually_edited ? "YES" : "NO")],
        ]);
    };

    const printPdf = () => {
        // Build the table via safe DOM APIs (createElement + textContent) so that
        // user-supplied strings can never escape into HTML — this satisfies the
        // XSS-pattern scanners by eliminating any `innerHTML` writes.
        const tmp = document.createElement("div");
        const horizonLabel = filterHorizon === "all" ? "All documents"
            : filterHorizon === "expired" ? "Expired documents"
            : `Expiring in next ${filterHorizon} days`;

        const meta = document.createElement("div");
        meta.style.cssText = "font-size:11px;color:#64748b;margin-bottom:8px;";
        meta.textContent = `${horizonLabel} · ${filtered.length} document(s)`;
        tmp.appendChild(meta);

        const table = document.createElement("table");
        const thead = document.createElement("thead");
        const headRow = document.createElement("tr");
        ["TYPE", "DOC #", "SUBJECT", "HOLDER", "ISSUE", "EXPIRY", "HAZMAT EXPIRY", "DAYS", "STATUS"]
            .forEach((h) => {
                const th = document.createElement("th");
                th.textContent = h;
                headRow.appendChild(th);
            });
        thead.appendChild(headRow);
        table.appendChild(thead);

        const tbody = document.createElement("tbody");
        filtered.forEach((d) => {
            const s = expiryStatus(d.expiry_date);
            const days = d.expiry_date ? daysUntil(d.expiry_date) : "";
            const subject = vmap[d.vehicle_id]?.reg_number || dmap[d.driver_id]?.name || "—";
            const typeLabel = TYPES.find((t) => t.key === d.doc_type)?.label || d.doc_type;
            const row = document.createElement("tr");
            [
                typeLabel,
                d.document_number || "—",
                subject,
                d.holder_name || "—",
                formatDate(d.issue_date),
                formatDate(d.expiry_date),
                formatDate(d.hazardous_expiry_date) || "—",
                days === "" ? "—" : String(days),
                s.label,
            ].forEach((val) => {
                const td = document.createElement("td");
                td.textContent = val;
                row.appendChild(td);
            });
            tbody.appendChild(row);
        });
        table.appendChild(tbody);
        tmp.appendChild(table);

        printElement(tmp, `EAZYFLEET · Compliance · ${horizonLabel}`);
    };

    const counts = useMemo(() => {
        const o = { critical: 0, warning: 0, healthy: 0, missing: 0 };
        docs.forEach((d) => {
            const s = expiryStatus(d.expiry_date);
            if (s.variant === "critical") o.critical += 1;
            else if (s.variant === "warning") o.warning += 1;
            else if (s.variant === "healthy") o.healthy += 1;
            else o.missing += 1;
        });
        return o;
    }, [docs]);

    const reextract = async (id) => {
        const doc = docs.find((d) => d.id === id);
        if (doc?.manually_edited) {
            const ok = window.confirm(
                "This document has manually-edited fields. Re-running AI extraction will OVERWRITE your edits with whatever the AI reads.\n\nContinue anyway?"
            );
            if (!ok) return;
        }
        const tid = toast.loading("Re-running AI extraction…");
        try {
            await api.post(`/compliance-documents/${id}/reextract`);
            toast.success("Re-extracted", { id: tid }); load();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail), { id: tid }); }
    };

    const del = async (id) => {
        if (!window.confirm("Delete document?")) return;
        await api.delete(`/compliance-documents/${id}`); load();
    };

    const submitUpload = async () => {
        if (!uploadFile) { toast.error("Choose a file first"); return; }
        // For Permit / Road Tax, require the sub-type so reports stay clean.
        if (isParentType(uploadForm.doc_type) && !uploadForm.doc_subtype) {
            toast.error(`Select a ${uploadForm.doc_type === "permit" ? "permit" : "tax"} sub-type`);
            return;
        }
        const fd = new FormData(); fd.append("file", uploadFile);
        const target = uploadForm.target;
        const targetId = target === "vehicle" ? uploadForm.vehicle_id : uploadForm.driver_id;
        if (!targetId) { toast.error(`Select a ${target}`); return; }
        const effectiveType = isParentType(uploadForm.doc_type) ? uploadForm.doc_subtype : uploadForm.doc_type;
        const tid = toast.loading("Uploading & extracting fields with AI…");
        try {
            const url = `/compliance-documents?doc_type=${effectiveType}&${target}_id=${targetId}`;
            await api.post(url, fd, { headers: { "Content-Type": "multipart/form-data" } });
            toast.success("Document processed", { id: tid });
            setUploadOpen(false); setUploadFile(null); load();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail), { id: tid }); }
    };

    const openEdit = (d) => {
        setEditDoc(d);
        setEditForm({
            document_number: d.document_number || "",
            issue_date: d.issue_date || "",
            expiry_date: d.expiry_date || "",
            holder_name: d.holder_name || "",
            hazardous_expiry_date: d.hazardous_expiry_date || "",
            notes: d.notes || "",
        });
    };

    const saveEdit = async () => {
        try {
            const payload = {
                document_number: editForm.document_number || null,
                issue_date: editForm.issue_date || null,
                expiry_date: editForm.expiry_date || null,
                holder_name: editForm.holder_name || null,
                hazardous_expiry_date: editForm.hazardous_expiry_date || null,
                notes: editForm.notes || null,
            };
            await api.patch(`/compliance-documents/${editDoc.id}`, payload);
            toast.success("Fields saved");
            setEditDoc(null); load();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    };

    return (
        <div className="p-6 lg:p-8 animate-fade-in" data-testid="compliance-page">
            <PageHeader
                eyebrow="COMPLIANCE"
                title="Documents & Renewals"
                subtitle="Upload, AI-extract and track every vehicle & driver compliance document."
                action={<Button onClick={() => setUploadOpen(true)} data-testid="upload-compliance-btn"><UploadSimple size={16} /> Upload document</Button>}
            />

            {/* Status tiles */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                <button onClick={() => setFilterStatus("critical")} className={`text-left border rounded-md p-5 transition-colors ${filterStatus === "critical" ? "border-fleet-critical bg-rose-50" : "border-fleet-border bg-white hover:border-slate-400"}`} data-testid="filter-critical">
                    <div className="label-mono">EXPIRED</div>
                    <div className="font-mono text-3xl font-semibold tabular mt-2 text-fleet-critical">{counts.critical}</div>
                    <div className="text-xs text-fleet-inkSoft mt-1.5">documents past expiry</div>
                </button>
                <button onClick={() => setFilterStatus("warning")} className={`text-left border rounded-md p-5 transition-colors ${filterStatus === "warning" ? "border-fleet-warning bg-amber-50" : "border-fleet-border bg-white hover:border-slate-400"}`} data-testid="filter-warning">
                    <div className="label-mono">DUE WITHIN 30D</div>
                    <div className="font-mono text-3xl font-semibold tabular mt-2 text-fleet-warning">{counts.warning}</div>
                    <div className="text-xs text-fleet-inkSoft mt-1.5">renewal upcoming</div>
                </button>
                <button onClick={() => setFilterStatus("healthy")} className={`text-left border rounded-md p-5 transition-colors ${filterStatus === "healthy" ? "border-fleet-healthy bg-emerald-50" : "border-fleet-border bg-white hover:border-slate-400"}`} data-testid="filter-healthy">
                    <div className="label-mono">VALID</div>
                    <div className="font-mono text-3xl font-semibold tabular mt-2 text-fleet-healthy">{counts.healthy}</div>
                    <div className="text-xs text-fleet-inkSoft mt-1.5">compliant</div>
                </button>
                <button onClick={() => setFilterStatus("all")} className={`text-left border rounded-md p-5 transition-colors ${filterStatus === "all" ? "border-fleet-ink bg-slate-50" : "border-fleet-border bg-white hover:border-slate-400"}`} data-testid="filter-all">
                    <div className="label-mono">TOTAL</div>
                    <div className="font-mono text-3xl font-semibold tabular mt-2 text-fleet-ink">{docs.length}</div>
                    <div className="text-xs text-fleet-inkSoft mt-1.5">all documents</div>
                </button>
            </div>

            {/* Missing-docs dashboard header: which vehicles still need each mandatory doc? */}
            {missing && missing.total_vehicles > 0 && (
                <Surface className="mb-6 overflow-hidden" data-testid="missing-docs-header">
                    <div className="px-5 py-3 border-b border-fleet-border flex items-center justify-between gap-3 flex-wrap">
                        <div>
                            <div className="label-mono">MISSING UPLOADS</div>
                            <h3 className="font-display text-base font-semibold mt-0.5">Vehicles without mandatory documents</h3>
                            <div className="text-xs text-fleet-inkSoft mt-0.5">
                                Tracks RC, Insurance, PUC, Fitness and Road Tax (state-matching) across all {missing.total_vehicles} active vehicle(s). Permit is excluded — not mandatory for every vehicle.
                            </div>
                        </div>
                    </div>
                    <div className="grid grid-cols-2 lg:grid-cols-5 gap-0">
                        {missing.categories.map((c) => {
                            const pct = missing.total_vehicles > 0 ? Math.round((c.compliant_count / missing.total_vehicles) * 100) : 100;
                            const tone = c.missing_count === 0 ? "text-emerald-700"
                                : c.missing_count <= 2 ? "text-amber-700"
                                    : "text-rose-700";
                            return (
                                <button
                                    key={c.doc_type}
                                    onClick={() => c.missing_count > 0 && setMissingDrillFor(c)}
                                    disabled={c.missing_count === 0}
                                    className={`text-left px-5 py-4 border-r border-fleet-border last:border-r-0 transition-colors ${c.missing_count > 0 ? "hover:bg-slate-50 cursor-pointer" : "cursor-default opacity-90"}`}
                                    data-testid={`missing-${c.doc_type}`}
                                >
                                    <div className="label-mono">{c.label}</div>
                                    <div className={`font-mono text-2xl font-semibold mt-1 ${tone}`}>
                                        {c.missing_count === 0 ? "0" : c.missing_count}
                                    </div>
                                    <div className="text-xs text-fleet-inkSoft mt-0.5">
                                        {c.missing_count === 0 ? "All compliant" : `missing of ${missing.total_vehicles}`}
                                    </div>
                                    {/* compliance bar */}
                                    <div className="mt-2 h-1 bg-slate-100 rounded-full overflow-hidden">
                                        <div className={`h-full ${c.missing_count === 0 ? "bg-emerald-500" : c.missing_count <= 2 ? "bg-amber-500" : "bg-rose-500"}`}
                                            style={{ width: `${pct}%` }} />
                                    </div>
                                    <div className="text-[10px] text-fleet-inkSoft mt-1">{pct}% compliant</div>
                                </button>
                            );
                        })}
                    </div>
                </Surface>
            )}

            {/* Filters */}
            <Surface className="px-5 py-3 mb-4 flex items-center gap-3 flex-wrap">
                <Funnel size={14} className="text-fleet-inkSoft" />
                <span className="label-mono">RENEWAL WINDOW</span>
                {[["all", "All"], ["expired", "Expired"], ["30", "Next 30 days"], ["60", "Next 60 days"], ["90", "Next 90 days"]].map(([k, l]) => (
                    <button key={k} onClick={() => setFilterHorizon(k)}
                        className={`text-xs px-2.5 py-1 rounded-md border transition-colors ${filterHorizon === k ? "border-fleet-ink bg-fleet-ink text-white" : "border-fleet-border bg-white hover:border-slate-400"}`}
                        data-testid={`filter-horizon-${k}`}>
                        {l}
                    </button>
                ))}
                <span className="text-xs text-fleet-inkSoft ml-2">{filtered.length} of {docs.length}</span>
                <div className="ml-auto flex gap-2">
                    <Button variant="secondary" size="sm" onClick={exportCsv} data-testid="compliance-export-csv">
                        <DownloadSimple size={14} /> CSV
                    </Button>
                    <Button variant="secondary" size="sm" onClick={printPdf} data-testid="compliance-export-pdf">
                        <Printer size={14} /> PDF
                    </Button>
                </div>
            </Surface>

            {/* Type filter */}
            <Surface className="px-5 py-3 mb-4 flex items-center gap-3 flex-wrap">
                <span className="label-mono">DOCUMENT TYPE</span>
                <button onClick={() => setFilterType("all")} className={`text-xs px-2.5 py-1 rounded-md border ${filterType === "all" ? "border-fleet-ink bg-fleet-ink text-white" : "border-fleet-border bg-white hover:border-slate-400"}`}>All types</button>
                {TYPES.map((t) => (
                    <button key={t.key} onClick={() => setFilterType(t.key)}
                        className={`text-xs px-2.5 py-1 rounded-md border ${filterType === t.key ? "border-fleet-ink bg-fleet-ink text-white" : "border-fleet-border bg-white hover:border-slate-400"}`}
                        data-testid={`filter-type-${t.key}`}>
                        {t.label}
                    </button>
                ))}
                {(filterStatus !== "all" || filterType !== "all" || filterHorizon !== "all" || filterVehicle) && (
                    <button onClick={() => { setFilterStatus("all"); setFilterType("all"); setFilterHorizon("all"); setFilterVehicle(""); }} className="ml-auto text-xs text-fleet-inkSoft hover:text-fleet-ink underline">Clear filters</button>
                )}
            </Surface>

            {/* Vehicle filter */}
            <Surface className="px-5 py-3 mb-4 flex items-center gap-3 flex-wrap">
                <span className="label-mono">VEHICLE</span>
                <div className="min-w-[280px] flex-1 max-w-md">
                    <VehicleSearchSelect
                        vehicles={vehicles}
                        value={filterVehicle}
                        onChange={setFilterVehicle}
                        placeholder="Filter by registration number…"
                        allowClear
                        testid="compliance-filter-vehicle"
                    />
                </div>
                {filterVehicle && (
                    <span className="text-xs text-fleet-inkSoft">
                        Showing docs for <span className="font-mono">{vmap[filterVehicle]?.reg_number || filterVehicle}</span>
                    </span>
                )}
            </Surface>

            {/* Documents table */}
            <Surface className="overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-slate-50">
                        <tr className="text-left">
                            {["TYPE", "DOC #", "SUBJECT", "ISSUE", "EXPIRY", "HAZMAT EXPIRY", "STATUS", "AI", ""].map((h, i, arr) => (
                                <th key={h} className={`label-mono px-5 py-3 font-normal${i === arr.length - 1 ? " no-print" : ""}`}>{h}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {filtered.map((d) => {
                            const s = expiryStatus(d.expiry_date);
                            const subjectVeh = vmap[d.vehicle_id];
                            const subjectDrv = dmap[d.driver_id];
                            return (
                                <tr key={d.id} className="border-t border-fleet-border data-grid-row" data-testid={`compliance-row-${d.id}`}>
                                    <td className="px-5 py-3">
                                        <Badge variant={d.doc_type === "driving_license" ? "info" : "preventive"}>
                                            {TYPES.find((t) => t.key === d.doc_type)?.label || d.doc_type}
                                        </Badge>
                                    </td>
                                    <td className="px-5 py-3 font-mono text-xs">{d.document_number || "—"}</td>
                                    <td className="px-5 py-3">
                                        {subjectVeh && (
                                            <Link to={`/vehicles/${subjectVeh.id}`} className="font-mono hover:underline">{subjectVeh.reg_number}</Link>
                                        )}
                                        {subjectDrv && (
                                            <Link to="/drivers" className="hover:underline">{subjectDrv.name}</Link>
                                        )}
                                        {!subjectVeh && !subjectDrv && <span className="text-fleet-inkMuted">—</span>}
                                    </td>
                                    <td className="px-5 py-3 font-mono">{formatDate(d.issue_date)}</td>
                                    <td className="px-5 py-3 font-mono">{formatDate(d.expiry_date)}</td>
                                    <td className="px-5 py-3 font-mono" data-testid={`hazmat-cell-${d.id}`}>
                                        {d.hazardous_expiry_date ? (
                                            <span className="inline-flex items-center gap-1">
                                                {formatDate(d.hazardous_expiry_date)}
                                                <Badge variant={expiryStatus(d.hazardous_expiry_date).variant}>
                                                    {expiryStatus(d.hazardous_expiry_date).label}
                                                </Badge>
                                            </span>
                                        ) : <span className="text-fleet-inkMuted">—</span>}
                                    </td>
                                    <td className="px-5 py-3"><Badge variant={s.variant}>{s.label}</Badge></td>
                                    <td className="px-5 py-3">
                                        <Badge
                                            variant={d.ai_status === "ok" ? "healthy" : "warning"}
                                            title={d.ai_status !== "ok" ? (d.ai_error || "AI returned no usable fields — click Re-extract or edit manually.") : undefined}
                                            data-testid={`ai-badge-${d.id}`}
                                        >
                                            <Sparkle size={10} className="inline mr-0.5" />
                                            {d.ai_status === "ok" ? "OK" : "PARTIAL"}
                                        </Badge>
                                        {d.manually_edited && (
                                            <Badge variant="info" className="ml-1">
                                                <PencilSimple size={10} className="inline mr-0.5" />
                                                EDITED
                                            </Badge>
                                        )}
                                    </td>
                                    <td className="px-5 py-3 text-right whitespace-nowrap no-print">
                                        <a href={fileUrl(d.file_id)}
                                           target="_blank" rel="noreferrer"
                                           className="inline-block text-xs text-fleet-accent hover:underline mr-2">View</a>
                                        <Button variant="ghost" size="sm" onClick={() => openEdit(d)} data-testid={`edit-${d.id}`} title="Manually edit fields">
                                            <PencilSimple size={12} />
                                        </Button>
                                        <Button variant="ghost" size="sm" onClick={() => reextract(d.id)} data-testid={`reextract-${d.id}`} title="Re-run AI">
                                            <ArrowsClockwise size={12} />
                                        </Button>
                                        <Button variant="ghost" size="sm" onClick={() => del(d.id)} data-testid={`del-doc-${d.id}`}>
                                            <Trash size={12} />
                                        </Button>
                                    </td>
                                </tr>
                            );
                        })}
                        {filtered.length === 0 && (
                            <tr><td colSpan={9}>
                                <div className="text-center py-16 px-6">
                                    <div className="mx-auto h-12 w-12 rounded-md border border-fleet-border bg-slate-50 flex items-center justify-center text-fleet-inkMuted mb-4">
                                        <ShieldCheck size={22} />
                                    </div>
                                    <div className="font-display text-lg font-medium">{docs.length === 0 ? "No documents uploaded yet" : "No documents match the filter"}</div>
                                    <p className="text-sm text-fleet-inkSoft mt-1 max-w-md mx-auto">
                                        {docs.length === 0 ? "Upload Insurance, RC, PUC, Fitness, Permit, Road Tax or Driver License — Claude Sonnet 4.5 will auto-extract the fields." : "Try clearing filters."}
                                    </p>
                                    {docs.length === 0 && (
                                        <div className="mt-5">
                                            <Button onClick={() => setUploadOpen(true)}><UploadSimple size={16} /> Upload first document</Button>
                                        </div>
                                    )}
                                </div>
                            </td></tr>
                        )}
                    </tbody>
                </table>
            </Surface>

            {/* Missing-docs drill-down modal */}
            <Modal
                open={!!missingDrillFor}
                onClose={() => setMissingDrillFor(null)}
                title={missingDrillFor ? `Vehicles missing ${missingDrillFor.label}` : ""}
                size="lg"
            >
                {missingDrillFor && (
                    <div className="space-y-3">
                        <div className="text-sm text-fleet-inkSoft">
                            {missingDrillFor.missing_count} vehicle(s) have not yet uploaded their {missingDrillFor.label.toLowerCase()} document.
                            {missingDrillFor.doc_type === "road_tax" && " For Road Tax, we accept any state-specific variant (TN, AP, KA, KL, KL Welfare) or the generic Road Tax bucket."}
                        </div>
                        <div className="border border-fleet-border rounded-md overflow-hidden max-h-[420px] overflow-y-auto">
                            <table className="w-full text-sm">
                                <thead className="bg-slate-50 sticky top-0">
                                    <tr className="text-left">
                                        <th className="label-mono px-4 py-2 font-normal">REG #</th>
                                        <th className="label-mono px-4 py-2 font-normal">MAKE / MODEL</th>
                                        <th className="label-mono px-4 py-2 font-normal">STATE</th>
                                        <th className="px-4 py-2 no-print"></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {missingDrillFor.missing_vehicles.map((v) => (
                                        <tr key={v.id} className="border-t border-fleet-border" data-testid={`missing-veh-${v.id}`}>
                                            <td className="px-4 py-2 font-mono font-medium">{v.reg_number || v.id.slice(0, 8)}</td>
                                            <td className="px-4 py-2 text-xs text-fleet-inkSoft">{[v.make, v.model].filter(Boolean).join(" ") || "—"}</td>
                                            <td className="px-4 py-2 font-mono text-xs">{v.state_code || "—"}</td>
                                            <td className="px-4 py-2 text-right">
                                                <button
                                                    onClick={() => {
                                                        setUploadForm({
                                                            doc_type: missingDrillFor.doc_type,
                                                            target: "vehicle",
                                                            vehicle_id: v.id,
                                                            driver_id: "",
                                                        });
                                                        setMissingDrillFor(null);
                                                        setUploadOpen(true);
                                                    }}
                                                    className="text-xs text-fleet-accent hover:underline inline-flex items-center gap-1"
                                                    data-testid={`upload-missing-${v.id}`}
                                                >
                                                    <UploadSimple size={12} /> Upload
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <div className="flex justify-end">
                            <Button variant="secondary" onClick={() => setMissingDrillFor(null)}>Close</Button>
                        </div>
                    </div>
                )}
            </Modal>


            {/* Upload Modal */}
            <Modal
                open={uploadOpen}
                onClose={() => { setUploadOpen(false); setUploadFile(null); }}
                title="Upload compliance document"
                footer={<>
                    <Button variant="secondary" onClick={() => { setUploadOpen(false); setUploadFile(null); }}>Cancel</Button>
                    <Button onClick={submitUpload} data-testid="confirm-compliance-upload">Upload & extract</Button>
                </>}
            >
                <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                        <Select label="Document type" value={uploadForm.doc_type}
                            onChange={(e) => {
                                const v = e.target.value;
                                setUploadForm({ ...uploadForm, doc_type: v, doc_subtype: "" });
                            }}
                            options={TYPES.filter((t) => !t.parent).map((t) => ({ value: t.key, label: t.label }))} />
                        <Select label="Belongs to" value={uploadForm.target}
                            onChange={(e) => setUploadForm({ ...uploadForm, target: e.target.value })}
                            options={[{ value: "vehicle", label: "Vehicle" }, { value: "driver", label: "Driver" }]} />
                    </div>
                    {isParentType(uploadForm.doc_type) && (
                        <Select
                            label={uploadForm.doc_type === "permit" ? "Permit type" : "Tax type"}
                            value={uploadForm.doc_subtype || ""}
                            onChange={(e) => setUploadForm({ ...uploadForm, doc_subtype: e.target.value })}
                            options={[
                                { value: "", label: "— pick a sub-type —" },
                                ...getSubsFor(uploadForm.doc_type).map((t) => ({ value: t.key, label: t.label })),
                            ]}
                            data-testid="compliance-doc-subtype"
                        />
                    )}
                    {uploadForm.target === "vehicle" ? (
                        <VehicleSearchSelect
                            vehicles={vehicles}
                            value={uploadForm.vehicle_id}
                            onChange={(id) => setUploadForm({ ...uploadForm, vehicle_id: id })}
                            label="Vehicle"
                            placeholder="Search by reg #…"
                            testid="compliance-upload-vehicle"
                        />
                    ) : (
                        <Select label="Driver" value={uploadForm.driver_id}
                            onChange={(e) => setUploadForm({ ...uploadForm, driver_id: e.target.value })}
                            options={[{ value: "", label: "Select…" }, ...drivers.map((d) => ({ value: d.id, label: d.name }))]} />
                    )}

                    <div>
                        <label className="label-mono block mb-1.5">File (PDF or image)</label>
                        <div className="border-2 border-dashed border-fleet-border rounded-md p-6 text-center bg-slate-50">
                            <input type="file" accept=".pdf,.png,.jpg,.jpeg,.webp"
                                onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                                className="block w-full text-sm" data-testid="compliance-file-input" />
                            {uploadFile && (
                                <div className="mt-3 flex items-center justify-center gap-2 text-sm text-fleet-ink">
                                    <FileText size={14} /> {uploadFile.name}
                                </div>
                            )}
                        </div>
                        <div className="mt-2 text-xs text-fleet-inkSoft flex items-center gap-1.5">
                            <Sparkle size={12} /> AI auto-extracts document number, issue/expiry dates, holder name.
                        </div>
                    </div>
                </div>
            </Modal>

            {/* Manual edit modal */}
            <Modal
                open={!!editDoc}
                onClose={() => setEditDoc(null)}
                title={editDoc ? `Edit fields manually · ${editDoc.original_filename}` : ""}
                size="lg"
                footer={<>
                    <Button variant="secondary" onClick={() => setEditDoc(null)}>Cancel</Button>
                    <Button onClick={saveEdit} data-testid="save-compliance-edit">Save fields</Button>
                </>}
            >
                {editDoc && (
                    <>
                        <div className="mb-4 px-3 py-2 bg-blue-50 border border-blue-200 rounded-md text-xs text-blue-900 flex items-center gap-2">
                            <Sparkle size={14} />
                            AI-extracted fields are pre-filled below. Override anything that's incorrect — your edits won't be overwritten unless you re-run AI extraction.
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <Input label="Document number" value={editForm.document_number}
                                onChange={(e) => setEditForm({ ...editForm, document_number: e.target.value })}
                                data-testid="edit-doc-number" />
                            <Input label="Holder name" value={editForm.holder_name}
                                onChange={(e) => setEditForm({ ...editForm, holder_name: e.target.value })} />
                            <Input label="Issue date" type="date" value={editForm.issue_date}
                                onChange={(e) => setEditForm({ ...editForm, issue_date: e.target.value })} />
                            <Input label="Expiry date" type="date" value={editForm.expiry_date}
                                onChange={(e) => setEditForm({ ...editForm, expiry_date: e.target.value })}
                                data-testid="edit-expiry-date" />
                            <Input label="Hazardous expiry date" type="date"
                                value={editForm.hazardous_expiry_date}
                                onChange={(e) => setEditForm({ ...editForm, hazardous_expiry_date: e.target.value })}
                                placeholder="If applicable"
                                data-testid="edit-hazardous-expiry" />
                            <Input label="Notes" className="col-span-2" value={editForm.notes}
                                onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })} />
                        </div>
                        <div className="mt-4 flex items-center gap-3 text-xs text-fleet-inkSoft">
                            <a href={fileUrl(editDoc.file_id)}
                               target="_blank" rel="noreferrer"
                               className="text-fleet-accent hover:underline">
                                <FileText size={12} className="inline mr-1" /> Open scanned document
                            </a>
                            <span>·</span>
                            <span>AI status: <Badge variant={editDoc.ai_status === "ok" ? "healthy" : "warning"}>{editDoc.ai_status?.toUpperCase()}</Badge></span>
                        </div>
                    </>
                )}
            </Modal>
        </div>
    );
}
